import { useState } from "react";
import styles from "./FooterSettings.module.css";
import { RotateCcw } from "lucide-react";

import FooterNavigation from "../../../../components/FooterSettings/FooterNavigation";
import FooterPaymentIcons from "../../../../components/FooterSettings/FooterPaymentIcons";
import FooterBrand from "../../../../components/FooterSettings/FooterBrand";
import FooterBottomBar from "../../../../components/FooterSettings/FooterBottomBar";
import FooterActions from "../../../../components/FooterSettings/FooterActionBar";
import BaseModal from "../../../../components/BaseComponents/BaseModal/BaseModal";
import { useToast } from "../../../../context/ToastContext";
import { useFooterConfig } from "../../../../hooks/useFooterConfig";
import { useUploadFooterBrandLogo } from "../../../../api/layout/useFooterConfigApi";
import "../../../../styles/cms-form-controls.css";

const FooterSettings = () => {
  const { showToast } = useToast();
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const uploadLogoApi = useUploadFooterBrandLogo();
  const {
    config,
    loading,
    saving,
    error,
    isDirty,
    updateField,
    setNavGroups,
    setPaymentIcons,
    save,
    reset,
  } = useFooterConfig();

  const handleReset = () => setShowResetConfirm(true);

  const handleResetConfirm = () => {
    reset();
    setShowResetConfirm(false);
  };

  const handleSave = async () => {
    try {
      await save();
      showToast("Footer settings saved successfully", "success");
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to save footer settings", "error");
    }
  };

  const handleCancel = () => {
    reset();
  };

  const handleUploadBrandLogo = async (file) => {
    try {
      const uploadedUrl = await uploadLogoApi.uploadLogo(file);
      if (uploadedUrl) {
        updateField("brandLogoUrl", uploadedUrl);
      }
      showToast("Footer brand logo uploaded successfully", "success");
    } catch (err) {
      showToast(
        err.response?.data?.message || "Failed to upload footer brand logo",
        "error"
      );
    }
  };

  if (loading) {
    return (
      <div className={`${styles.page} cms-form-scope`}>
        <div className={styles.pageHeader}>
          <div className={styles.headerLeft}>
            <div className={styles.headerIcon}>≡</div>
            <div>
              <h1>CMS - Footer Configuration</h1>
              <p>Loading…</p>
            </div>
          </div>
        </div>
        <p style={{ padding: 24, textAlign: "center", color: "#6b7280" }}>
          Loading footer configuration…
        </p>
      </div>
    );
  }

  return (
    <div className={`${styles.page} cms-form-scope`}>
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}>≡</div>
          <div>
            <h1>CMS - Footer Configuration</h1>
            <p>Configure everything that appears in the site footer</p>
          </div>
        </div>
        <button type="button" className={styles.resetBtn} onClick={handleReset}>
          <RotateCcw size={14} />
          Reset All
        </button>
      </div>

      <BaseModal
        open={showResetConfirm}
        onClose={() => setShowResetConfirm(false)}
        title="Reset footer settings"
        width="440px"
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
            <button
              type="button"
              onClick={() => setShowResetConfirm(false)}
              style={{
                padding: "10px 20px",
                borderRadius: 10,
                border: "1px solid #e5e7eb",
                background: "#fff",
                fontSize: 15,
                fontWeight: 600,
                color: "#374151",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleResetConfirm}
              style={{
                padding: "10px 20px",
                borderRadius: 10,
                border: "none",
                background: "#f5a51e",
                color: "#fff",
                fontSize: 15,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Reset
            </button>
          </div>
        }
      >
        <p style={{ margin: 0, fontSize: 15, lineHeight: 1.5, color: "#374151" }}>
          Reset all footer settings to last saved? Your unsaved changes will be discarded.
        </p>
      </BaseModal>

      {error && (
        <div
          className={styles.card}
          style={{
            marginBottom: 16,
            background: "#fef2f2",
            border: "1px solid #fecaca",
          }}
        >
          <p style={{ margin: 0, color: "#b91c1c" }}>{error}</p>
        </div>
      )}

      <FooterBrand
        brandName={config.brandName}
        setBrandName={(v) => updateField("brandName", v)}
        brandDescription={config.brandDescription}
        setBrandDescription={(v) => updateField("brandDescription", v)}
        brandLogoUrl={config.brandLogoUrl}
        setBrandLogoUrl={(v) => updateField("brandLogoUrl", v)}
        onUploadLogo={handleUploadBrandLogo}
        uploadLoading={uploadLogoApi.loading}
        uploadError={uploadLogoApi.error}
      />
      <FooterNavigation data={config.navigationSections} setData={setNavGroups} />
      <FooterPaymentIcons icons={config.paymentIcons} setIcons={setPaymentIcons} />
      <FooterBottomBar
        copyrightText={config.copyrightText}
        setCopyrightText={(v) => updateField("copyrightText", v)}
      />

      <FooterActions
        saved={!isDirty && !saving}
        saving={saving}
        onCancel={handleCancel}
        onSave={handleSave}
      />
    </div>
  );
};

export default FooterSettings;
